import { Overline, Section, Pill } from "../components/Bits";
import { Sprout, Truck, Heart, Globe2, ChefHat, Soup, Boxes, ScrollText, ShieldCheck, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { BRAND } from "../lib/gomill-data";

const PILLARS = [
  { icon: Sprout, t: "Millet-First", d: "Wholesome ancient grains, made modern." },
  { icon: ShieldCheck, t: "Clean & Honest", d: "No shortcuts. Transparent kitchens, real ingredients." },
  { icon: Truck, t: "Mobile & Modern", d: "We meet you where you are, every day." },
  { icon: Heart, t: "Made for India", d: "Built for our streets, festivals, families." },
];

const HOW = [
  { n: "01", t: "Curated Ingredients", d: "Local farmers. Slow-mill, no preservatives.", icon: Sprout },
  { n: "02", t: "Cooked with Care", d: "Trained chefs follow standard recipes.", icon: ChefHat },
  { n: "03", t: "Quality Checked", d: "50+ checks every day across kitchens.", icon: ShieldCheck },
  { n: "04", t: "Packed Safely", d: "Eco-friendly, food-grade packaging.", icon: Boxes },
  { n: "05", t: "Delivered Fresh", d: "Trucks, kiosks, doorstep — all under 30 min.", icon: Truck },
];

const JOURNEY = [
  { t: "The Inspiration", d: "Why is street food fun but not always safe?" },
  { t: "The Idea", d: "Bring branded trust to India's favourite cravings." },
  { t: "The First Truck", d: "Hyderabad, 2026. One green truck. One mission." },
  { t: "Today", d: "Growing steadily. Expanding our reach. Driving a greener tomorrow." },
  { t: "The Road Ahead", d: "100 cities. Daily plans for a million Indians." },
];

export default function About() {
  return (
    <>
      <Section>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <Overline className="mb-4">Our Story</Overline>
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl text-gomill-green leading-[1.02] tracking-tight">
              Millets, made <span className="italic">mobile.</span> Modern. Mainstream.
            </h1>
            <p className="mt-5 text-lg text-gomill-muted max-w-xl">
              GoMill was born from a simple belief — great food can be quick, clean and deeply nourishing. We bring the timeless goodness of millets to today's fast-moving world. A Native Touch food-on-the-go brand, proudly backed by <strong>{BRAND.parent}</strong>.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/menu" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-gomill-green text-white hover:bg-gomill-leaf font-semibold" data-testid="about-order">
                Order Now
              </Link>
              <Link to="/find" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-gomill-mustard text-gomill-green hover:bg-gomill-mustardDark font-semibold" data-testid="about-find">
                Find GoMill Near You
              </Link>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="aspect-square rounded-[32px] overflow-hidden bg-gomill-cream">
              <img src={BRAND.heroTruck} alt="GoMill story" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </Section>

      {/* Editorial pull quote */}
      <Section>
        <div className="max-w-4xl mx-auto text-center">
          <ScrollText className="w-8 h-8 mx-auto text-gomill-mustard" />
          <blockquote className="mt-6 font-serif text-3xl md:text-5xl text-gomill-green leading-tight italic">
            "Outside food <span className="not-italic">·</span> inside trust. <br/>That's the GoMill way."
          </blockquote>
        </div>
      </Section>

      {/* Pillars */}
      <Section className="bg-gomill-cream rounded-3xl mx-3 md:mx-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end mb-10">
          <div className="md:col-span-7">
            <Overline className="mb-3">What Makes GoMill, GoMill</Overline>
            <h2 className="font-serif text-4xl md:text-5xl text-gomill-green leading-tight">Four pillars. One promise.</h2>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {PILLARS.map((p) => (
            <div key={p.t} className="bg-white rounded-3xl p-7 border border-gomill-border hover:-translate-y-1 transition-all">
              <div className="w-12 h-12 rounded-2xl bg-gomill-green text-gomill-mustard flex items-center justify-center mb-4">
                <p.icon className="w-5 h-5" />
              </div>
              <div className="font-serif text-2xl text-gomill-green">{p.t}</div>
              <p className="text-sm text-gomill-muted mt-2">{p.d}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* Journey timeline */}
      <Section>
        <Overline className="mb-3">Our Journey</Overline>
        <h2 className="font-serif text-4xl md:text-5xl text-gomill-green leading-tight mb-10">A small truck. A big idea.</h2>
        <div className="relative">
          <div className="absolute left-3 md:left-1/2 top-0 bottom-0 w-px bg-gomill-border" />
          <div className="space-y-10">
            {JOURNEY.map((j, i) => (
              <div key={j.t} className={`relative pl-10 md:pl-0 md:grid md:grid-cols-2 md:gap-10 ${i % 2 === 0 ? "" : "md:[direction:rtl]"}`}>
                <div className={`md:[direction:ltr]`}>
                  <div className="bg-white border border-gomill-border rounded-3xl p-6">
                    <Pill tone={i === 2 ? "mustard" : "leaf"}>{`Stage 0${i+1}`}</Pill>
                    <div className="font-serif text-2xl text-gomill-green mt-3">{j.t}</div>
                    <p className="text-gomill-muted mt-2">{j.d}</p>
                  </div>
                </div>
                <div className="hidden md:block" />
                <span className="absolute left-1.5 md:left-1/2 -translate-x-1/2 top-6 w-3 h-3 rounded-full bg-gomill-mustard ring-4 ring-gomill-bg" />
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* HOW WE DO IT */}
      <Section className="bg-gomill-green text-white rounded-3xl mx-3 md:mx-12">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold text-gomill-mustard">How we do it</span>
          <h2 className="font-serif text-4xl md:text-5xl mt-4 leading-tight">From kitchen to doorstep.</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {HOW.map((h) => (
            <div key={h.n} className="bg-white/5 border border-white/10 rounded-3xl p-5">
              <div className="font-serif text-4xl text-gomill-mustard">{h.n}</div>
              <div className="font-serif text-xl mt-2">{h.t}</div>
              <p className="text-sm text-white/70 mt-2">{h.d}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* EBG metrics */}
      <Section>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-5">
            <Overline className="mb-3">Backed by EBG Group</Overline>
            <h2 className="font-serif text-4xl md:text-5xl text-gomill-green leading-tight">Trust, decades in the making.</h2>
            <p className="mt-4 text-gomill-muted max-w-md">GoMill stands on a legacy of food, hospitality and care. EBG Group has been building consumer brands India loves for half a century.</p>
            <a href="#" className="mt-6 inline-flex items-center gap-1 text-gomill-green font-semibold hover:text-gomill-leaf">Know more about EBG Group <ArrowRight className="w-4 h-4" /></a>
          </div>
          <div className="md:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              { icon: Sprout, t: "Millet-Led", d: "Wholesome Nutrition" },
              { icon: ChefHat, t: "Freshly Prepared", d: "Made Daily" },
              { icon: Heart, t: "No Reused Oil", d: "Healthier Cooking" },
              { icon: ShieldCheck, t: "Hygienic Kitchens", d: "Quality Assured" },
            ].map((c) => (
              <div key={c.t} className="bg-gomill-cream rounded-3xl p-6 border border-gomill-border">
                <div className="w-11 h-11 rounded-2xl bg-gomill-green text-gomill-mustard flex items-center justify-center mb-4">
                  <c.icon className="w-5 h-5" />
                </div>
                <div className="font-serif text-xl text-gomill-green leading-tight">{c.t}</div>
                <p className="text-sm text-gomill-muted mt-1">{c.d}</p>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* Sustainability */}
      <Section>
        <div className="bg-gomill-mustard rounded-3xl p-10 md:p-14 grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-7">
            <Overline className="mb-3">Good for you. Good for our planet.</Overline>
            <h2 className="font-serif text-4xl md:text-5xl text-gomill-green leading-tight">Millets. Local. Loved.</h2>
            <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3 text-gomill-green">
              {["Sourced from local farmers", "100% Veg. No preservatives.", "Sustainable. Responsible.", "Loved by all ages."].map((t) => (
                <li key={t} className="flex items-start gap-2"><Soup className="w-4 h-4 mt-1" /> {t}</li>
              ))}
            </ul>
          </div>
          <div className="md:col-span-5 hidden md:block">
            <Globe2 className="w-32 h-32 mx-auto text-gomill-green/30" />
          </div>
        </div>
      </Section>
    </>
  );
}
